import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, input, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import type { Observable, TimeInterval } from 'rxjs';
import { interval, of, timer, combineLatest, pairwise } from 'rxjs';
import {
  delayWhen,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
  takeUntil,
  timeInterval,
} from 'rxjs/operators';

import { TranslatePipe } from '@core/i18n-legacy';
import { SpinnerComponent } from '@core/user-interface-legacy';
import { injectUntilDestroyed } from '@core/utils-legacy';

@Component({
  selector: 'reports-visualization-loading-indicator',
  styleUrls: ['./visualization-loading-indicator.component.less'],
  template: `
    @let spinnerVisible = spinnerVisible$ | async;
    @let timer = (timer$ | async) ?? 0;
    <div
      [class.small]="small()"
      [class.initial]="!successfulLoad() && spinnerVisible"
      [class.subsequent]="successfulLoad() && spinnerVisible"
      [class.absolute]="absolutePosition()"
      class="host"
    >
      @if (spinnerVisible) {
        <rt-spinner />
        @if (timer >= LONG_LOADING_THRESHOLD_SECONDS && displayLongLoadingMsg()) {
          <span class="long-loading">{{
            'tlr.grid_component.crunching_that_data' | translate
          }}</span>
        }
      }
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, SpinnerComponent, TranslatePipe],
  host: {
    '[class.loaded]': 'loaded()',
    '[class.subsequent-spinner-top-adjusted]': 'moveSubsequentSpinnerTop()',
  },
})
export class VisualizationLoadingIndicatorComponent {
  private readonly MIN_LOADING_TIME = 250;
  private readonly untilDestroyed = injectUntilDestroyed();

  public readonly LONG_LOADING_THRESHOLD_SECONDS = 4;

  public loading = input.required<boolean>();
  public loaded = input.required<boolean>();
  public failed = input.required<boolean>();
  public small = input.required<boolean>();
  public absolutePosition = input(true);
  public displayLongLoadingMsg = input(true);
  public moveSubsequentSpinnerTop = input(true);

  public loading$ = toObservable(this.loading);
  public spinnerVisible$: Observable<boolean> = this.loading$.pipe(
    distinctUntilChanged(),
    timeInterval(),
    delayWhen((x: TimeInterval<boolean>) =>
      x.value === false ? timer(this.MIN_LOADING_TIME - x.interval) : timer(0),
    ),
    switchMap(x => of(x.value)),
    shareReplay(1),
    startWith(true),
    this.untilDestroyed,
  );
  public timer$: Observable<number>;
  public successfulLoad = signal(false);

  constructor() {
    const timerStart$ = this.spinnerVisible$.pipe(filter(x => x === true));
    const timerEnd$ = this.spinnerVisible$.pipe(filter(x => x === false));
    this.timer$ = timerStart$.pipe(
      switchMap(() =>
        interval(1000).pipe(
          startWith(-1),
          map(i => i + 1),
          takeUntil(timerEnd$),
        ),
      ),
      shareReplay(1),
      this.untilDestroyed,
    );

    this.timer$.subscribe();

    const failed$ = toObservable(this.failed);
    const prevFailed$ = failed$.pipe(
      startWith(null),
      pairwise(),
      map(([prev, curr]) => prev),
    );

    combineLatest([
      this.spinnerVisible$,
      failed$,
      prevFailed$,
      toObservable(this.loaded),
    ]).subscribe(([spinnerVisible, failed, prevFailed, loaded]) => {
      if (prevFailed === true && failed === false) {
        this.successfulLoad.set(false);
      }
      if (!spinnerVisible && !failed && loaded) {
        this.successfulLoad.set(true);
      }
    });
  }
}
